import { Head, useForm } from "@inertiajs/react";
import { LoaderCircle } from "lucide-react";
import type { FormEventHandler } from "react";

import InputError from "@/components/input-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthLayout from "@/layouts/auth-layout";
import { identityPasswordResetPath } from "@/routes";

interface ResetPasswordProps {
  sid: string;
  email: string;
}

interface ResetPasswordForm {
  sid: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export default function ResetPassword({ sid, email }: ResetPasswordProps) {
  const { data, setData, put, processing, errors, reset } = useForm<
    Required<ResetPasswordForm>
  >({
    sid: sid,
    email: email,
    password: "",
    password_confirmation: "",
  });

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    put(identityPasswordResetPath(), {
      onFinish: () => reset("password", "password_confirmation"),
    });
  };

  return (
    <AuthLayout
      title="Crea tu nueva contraseña"
      description="Ingresa tu nueva contraseña a continuación"
    >
      <Head title="Crear nueva contraseña" />

      <form onSubmit={submit}>
        <div className="grid gap-6">
          <div className="grid gap-2">
            <Label htmlFor="email">Correo electrónico</Label>
            <Input
              id="email"
              type="email"
              name="email"
              autoComplete="email"
              value={data.email}
              className="mt-1 block w-full"
              readOnly
              onChange={(e) => setData("email", e.target.value)}
            />
            <InputError message={errors.email} className="mt-2" />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="password">Nueva contraseña</Label>
            <Input
              id="password"
              type="password"
              name="password"
              autoComplete="new-password"
              value={data.password}
              className="mt-1 block w-full"
              autoFocus
              onChange={(e) => setData("password", e.target.value)}
              placeholder="Tu nueva contraseña"
            />
            <InputError message={errors.password} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="password_confirmation">Confirma tu nueva contraseña</Label>
            <Input
              id="password_confirmation"
              type="password"
              name="password_confirmation"
              autoComplete="new-password"
              value={data.password_confirmation}
              className="mt-1 block w-full"
              onChange={(e) => setData("password_confirmation", e.target.value)}
              placeholder="Ingresa la misma contraseña nuevamente"
            />
            <InputError
              message={errors.password_confirmation}
              className="mt-2"
            />
          </div>

          <Button type="submit" className="mt-4 w-full" disabled={processing}>
            {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
            Guardar nueva contraseña
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
